<template>
  <div class="page">
    <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;">
      <h2 class="page-title" style="margin:0;">{{ $t('menu.handlerInProgress') }}</h2>
      <div style="display:flex;gap:8px;">
        <el-button type="warning" plain @click="doBatchRelease" :loading="batchReleasing" :disabled="!hasCases">{{ $t('handler.batchRelease') }}</el-button>
        <el-button @click="load" :loading="loading">{{ $t('common.refresh') }}</el-button>
      </div>
    </div>

    <div class="glass-card" style="margin-top: 14px; padding: 14px;">
      <el-table :data="itemsPage" style="width:100%;" @row-click="open">
        <el-table-column prop="kindLabel" :label="$t('field.type')" width="110" />
        <el-table-column prop="caseNo" :label="$t('field.caseNo')" width="140" />
        <el-table-column prop="title" :label="$t('field.title')" />
        <el-table-column :label="$t('field.status')" width="170">
          <template slot-scope="s">
            <span class="status-pill-wrap">
              <span :class="['tag-pill', s.row.hasReminder ? 'tag-pill--with-dot' : '']">{{ rowStatusLabel(s.row) }}</span>
              <span v-if="s.row.hasReminder" class="status-pill-dot" />
            </span>
          </template>
        </el-table-column>
        <el-table-column :label="flowColumnLabel" width="220">
          <template slot-scope="s">
            <span class="tag-pill">{{ rowFlowLabel(s.row) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('field.timeout')" width="140">
          <template slot-scope="s">
            <span :class="['tag-pill', isTimeout24(s.row) ? 'tag-pill--danger' : '']">{{ isTimeout24(s.row) ? $t('handler.timeout24') : '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="takenAtLabel" :label="$t('field.takenAt')" width="190" />
      </el-table>

      <div style="display:flex;justify-content:flex-end;margin-top:12px;">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :total="items.length"
          :page-size="pageSize"
          :current-page="page"
          :page-sizes="[5,10,20,50]"
          @current-change="(p) => (page = p)"
          @size-change="onSizeChange"
        />
      </div>
    </div>

    <HandlerCaseDialog v-model="detailDialog" :case-no="currentCaseNo" @reminder-read="onReminderRead" />
  </div>
</template>

<script>
import { handlerInProgress, batchReleaseCases } from '../../api/tickets';
import { handlerDisputeInProgress } from '../../api/disputes';
import HandlerCaseDialog from '../../components/HandlerCaseDialog.vue';
import { caseStatusLabel as formatCaseStatusLabel, flowLabel as formatFlowLabel, localText as formatLocalText } from '../../utils/caseDisplay';

export default {
  name: 'HandlerInProgress',
  components: { HandlerCaseDialog },
  data() {
    return { loading: false, batchReleasing: false, items: [], page: 1, pageSize: 10, detailDialog: false, currentCaseNo: '', nowTs: Date.now(), tickTimer: null };
  },
  created() {
    this.load();
    this.maybeOpenFromQuery();
    this.tickTimer = setInterval(() => {
      this.nowTs = Date.now();
    }, 60 * 1000);
  },
  beforeDestroy() {
    if (this.tickTimer) clearInterval(this.tickTimer);
    this.tickTimer = null;
  },
  computed: {
    version() {
      return this.$store.state.versions.handlerCases;
    },
    disputeVersion() {
      return this.$store.state.versions.disputes;
    },
    itemsPage() {
      const start = (this.page - 1) * this.pageSize;
      return (this.items || []).slice(start, start + this.pageSize);
    },
    hasCases() {
      return (this.items || []).some((x) => x.kind === 'CASE');
    },
    flowColumnLabel() {
      return formatLocalText(this, '流转', 'Flow');
    }
  },
  watch: {
    version() {
      this.load();
    },
    disputeVersion() {
      this.load();
    },
    detailDialog(v) {
      if (v) return;
      if (this.$route.query && this.$route.query.caseNo) {
        const q = { ...this.$route.query };
        delete q.caseNo;
        this.$router.replace({ path: this.$route.path, query: q });
      }
    },
    '$route.query.caseNo'() {
      this.maybeOpenFromQuery();
    }
  },
  methods: {
    onSizeChange(s) {
      this.pageSize = s;
      this.page = 1;
    },
    isTimeout24(row) {
      if (!row || !row.takenAt) return false;
      return this.nowTs - new Date(row.takenAt).getTime() > 24 * 60 * 60 * 1000;
    },
    rowStatusLabel(row) {
      if (row && row.kind === 'DISPUTE') {
        const key = `status.dispute.${row.status}`;
        try {
          if (this.$i18n && this.$i18n.te && this.$i18n.te(key)) return this.$t(key);
        } catch (e) {}
        return row.status || '-';
      }
      return formatCaseStatusLabel(this, row);
    },
    rowFlowLabel(row) {
      if (row && row.kind === 'DISPUTE') return formatLocalText(this, '争议处理中', 'Dispute in progress');
      return formatFlowLabel(this, row);
    },
    async load() {
      if (!this.$store.state.user) return;
      this.loading = true;
      try {
        const [cases, disputes] = await Promise.all([handlerInProgress(), handlerDisputeInProgress()]);
        const caseItems = (cases || []).map((x) => ({
          ...x,
          kind: 'CASE',
          kindLabel: this.$t('menu.tickets'),
          takenAtLabel: x.takenAt ? new Date(x.takenAt).toLocaleString() : '-'
        }));
        const disputeItems = (disputes || []).map((x) => ({
          kind: 'DISPUTE',
          kindLabel: this.$t('ticket.disputeLabel'),
          id: x.id,
          caseNo: x.caseNo,
          title: x.caseTitle || x.caseNo || '-',
          status: x.status,
          takenAt: x.takenAt,
          takenAtLabel: x.takenAt ? new Date(x.takenAt).toLocaleString() : '-',
          hasReminder: !!x.hasReminder
        }));
        this.items = [...caseItems, ...disputeItems].filter((x) => !!x.caseNo);
        this.items.sort((a, b) => {
          const ta = a.takenAt ? new Date(a.takenAt).getTime() : 0;
          const tb = b.takenAt ? new Date(b.takenAt).getTime() : 0;
          return tb - ta;
        });
        const maxPage = Math.max(1, Math.ceil(this.items.length / this.pageSize));
        this.page = Math.min(this.page, maxPage);
      } finally {
        this.loading = false;
      }
    },
    maybeOpenFromQuery() {
      const caseNo = this.$route.query && this.$route.query.caseNo ? String(this.$route.query.caseNo) : '';
      if (!caseNo) return;
      this.currentCaseNo = caseNo;
      this.detailDialog = true;
    },
    open(row) {
      if (!row || !row.caseNo) return;
      this.currentCaseNo = row.caseNo;
      this.detailDialog = true;
    },
    onReminderRead(caseNo) {
      if (!caseNo) return;
      this.items = (this.items || []).map((x) => String(x.caseNo) === String(caseNo) ? { ...x, hasReminder: false } : x);
      this.$store.dispatch('refreshHandlerBadges');
    },
    async doBatchRelease() {
      try {
        await this.$confirm(this.$t('handler.batchReleaseConfirm'), { type: 'warning' });
      } catch (e) {
        return;
      }
      this.batchReleasing = true;
      try {
        const r = await batchReleaseCases();
        this.$message.success(`${this.$t('handler.batchRelease')}: ${r.released || 0}`);
        this.$store.commit('bumpVersion', 'handlerCases');
        this.$store.dispatch('refreshHandlerBadges');
      } catch (e) {} finally {
        this.batchReleasing = false;
      }
    }
  }
};
</script>

<style scoped>
.status-pill-wrap {
  position: relative;
  display: inline-block;
}
.tag-pill--with-dot {
  padding-right: 18px;
}
.status-pill-dot {
  position: absolute;
  top: 3px;
  right: 5px;
  width: 7px;
  height: 7px;
  border-radius: 99px;
  background: rgba(239, 68, 68, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.95);
}
</style>
